/**
 * Pinned legacy type surface for the historical (v1–v9) loaded-state codecs.
 *
 * The v1–v9 encode/decode files decode a FROZEN wire format. Before the
 * ComponentSelection union-collapse refactor they depended on these types,
 * guards and the `startContextSelectionType` sentinel, all of which were
 * deleted from `@ardoq/api-types`. Rather than rewrite the historical codecs to
 * chase the evolving domain model (which couples frozen code to live types and
 * forces a per-decoder decision every time the model changes), we re-introduce
 * exactly what they need here, and translate legacy <-> canonical in a single
 * adapter at the dispatcher boundary (see ./adapter.ts).
 *
 * Only the ComponentSelection shape actually changed, so the param wrappers are
 * derived from the canonical types via Omit and only swap `componentSelection`.
 * Everything else (paths, filters, path matching, path collapsing) is shared
 * wire shape and intentionally tracks the canonical types.
 */
import {
  LoadedStateType,
  QueryBuilderQuery,
  TraversalParams as CanonicalTraversalParams,
  SearchLoadedStateParams as CanonicalSearchLoadedStateParams,
  TraversalLoadedStateParams as CanonicalTraversalLoadedStateParams,
  TraversalCreatedInViewLoadedStateParams as CanonicalTraversalCreatedInViewLoadedStateParams,
  CreatedItemsLoadedStateParams,
} from '@ardoq/api-types';

export type { CreatedItemsLoadedStateParams };

/**
 * @deprecated Frozen legacy v1–v9 codec sentinel. Removed from the canonical
 * model during the ComponentSelection union-collapse — do not use in new code.
 */
export enum StartContextSelectionType {
  MANUAL_SELECTION = 1,
  BASIC_SEARCH_SELECT_ALL,
  ADVANCED_SEARCH_SELECT_ALL,
}

/**
 * @deprecated Frozen legacy v1–v9 codec type. Use the canonical
 * `ComponentSelection` from `@ardoq/api-types` instead.
 */
export type ComponentSelection = {
  startSet?: string[];
  startQuery?: QueryBuilderQuery;
  startContextSelectionType?: StartContextSelectionType;
};

/**
 * @deprecated Frozen legacy v1–v9 codec type. The canonical model collapsed this
 * union — use the canonical `ComponentSelection` from `@ardoq/api-types` instead.
 */
export type ManualComponentSelection = ComponentSelection & {
  startSet: string[];
};

/**
 * @deprecated Frozen legacy v1–v9 codec type. The canonical model collapsed this
 * union — use the canonical `ComponentSelection` from `@ardoq/api-types` instead.
 */
export type QueryComponentSelection = ComponentSelection & {
  startQuery: QueryBuilderQuery;
};

/**
 * @deprecated Frozen legacy v1–v9 codec type. Use the canonical `TraversalParams`
 * from `@ardoq/api-types` instead.
 */
export type TraversalParams = Omit<
  CanonicalTraversalParams,
  'componentSelection'
> & {
  componentSelection: ComponentSelection;
};
/**
 * @deprecated Frozen legacy v1–v9 codec alias. Use the canonical `TraversalParams`
 * from `@ardoq/api-types` instead.
 */
export type Traversal = TraversalParams;
/**
 * @deprecated Frozen legacy v1–v9 codec alias. Use the canonical `TraversalParams`
 * from `@ardoq/api-types` instead.
 */
export type StartSetTraversalParams = TraversalParams;
/**
 * @deprecated Frozen legacy v1–v9 codec alias. Use the canonical `TraversalParams`
 * from `@ardoq/api-types` instead.
 */
export type StartQueryTraversalParams = TraversalParams;

/**
 * @deprecated Frozen legacy v1–v9 codec type. Use the canonical
 * `SearchLoadedStateParams` from `@ardoq/api-types` instead.
 */
export type SearchLoadedStateParams = Omit<
  CanonicalSearchLoadedStateParams,
  'data'
> & {
  data: { componentSelection: ComponentSelection };
};

/**
 * @deprecated Frozen legacy v1–v9 codec type. Use the canonical
 * `TraversalLoadedStateParams` from `@ardoq/api-types` instead.
 */
export type TraversalLoadedStateParams = Omit<
  CanonicalTraversalLoadedStateParams,
  'data'
> & {
  data: TraversalParams;
};

/**
 * @deprecated Frozen legacy v1–v9 codec type. Use the canonical
 * `TraversalCreatedInViewLoadedStateParams` from `@ardoq/api-types` instead.
 */
export type TraversalCreatedInViewLoadedStateParams = Omit<
  CanonicalTraversalCreatedInViewLoadedStateParams,
  'data'
> & {
  data: TraversalParams;
};

/**
 * @deprecated Frozen legacy v1–v9 codec type. Use the canonical
 * `LoadedStateParams` from `@ardoq/api-types` instead.
 */
export type LoadedStateParams =
  | SearchLoadedStateParams
  | TraversalLoadedStateParams
  | CreatedItemsLoadedStateParams
  | TraversalCreatedInViewLoadedStateParams;

const hasStartQuery = (
  componentSelection: ComponentSelection
): componentSelection is QueryComponentSelection =>
  Boolean(componentSelection.startQuery);

/**
 * @deprecated Frozen legacy v1–v9 codec guard, operating on the legacy
 * `ComponentSelection`. Do not use in new code.
 */
export const isManualComponentSelection = (
  componentSelection: ComponentSelection
): componentSelection is ManualComponentSelection =>
  !hasStartQuery(componentSelection);

/**
 * @deprecated Frozen legacy v1–v9 codec guard, operating on the legacy
 * `ComponentSelection`. Do not use in new code.
 */
export const isQueryComponentSelection = (
  componentSelection: ComponentSelection
): componentSelection is QueryComponentSelection =>
  hasStartQuery(componentSelection);

/**
 * @deprecated Frozen legacy v1–v9 codec guard. Use the canonical
 * `isSearchLoadedState` from `@ardoq/api-types` instead.
 */
export const isSearchLoadedState = (
  state: LoadedStateParams
): state is SearchLoadedStateParams => state.type === LoadedStateType.SEARCH;

/**
 * @deprecated Frozen legacy v1–v9 codec guard. Use the canonical
 * `isCreatedItemsLoadedState` from `@ardoq/api-types` instead.
 */
export const isCreatedItemsLoadedState = (
  state: LoadedStateParams
): state is CreatedItemsLoadedStateParams =>
  state.type === LoadedStateType.CREATED_ITEMS;

/**
 * @deprecated Frozen legacy v1–v9 codec guard. Use the canonical
 * `isTraversalLoadedState` from `@ardoq/api-types` instead.
 */
export const isTraversalLoadedState = (
  state: LoadedStateParams
): state is TraversalLoadedStateParams =>
  state.type === LoadedStateType.TRAVERSAL;

/**
 * @deprecated Frozen legacy v1–v9 codec guard. Use the canonical
 * `isAnyTraversalLoadedState` from `@ardoq/api-types` instead.
 */
export const isAnyTraversalLoadedState = (
  state: LoadedStateParams
): state is
  | TraversalLoadedStateParams
  | TraversalCreatedInViewLoadedStateParams =>
  state.type === LoadedStateType.TRAVERSAL ||
  state.type === LoadedStateType.TRAVERSAL_CREATED_IN_VIEW;
